'use client';

import {
  Alert,
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { FormTextField } from '@/components/form/formTextField';
import {
  RedshiftNodeTypeSelect,
  RedshiftNodeCountSelect,
} from '@/components/selectors/redshiftNodeTypeSelect';
import { RedshiftServerlessNamespaceSelect } from '@/components/selectors/redshiftServerlessNamespaceSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreRedshiftState } from '../restoreRedshiftWizard';

const requiredValidation = {
  validateInput: (v: string) => !!v?.trim(),
  errorMessage: 'Required' as const,
};

export const RedshiftConfiguration = (
  props: StepProps<RestoreRedshiftState>
) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  const hasSourceAccount = !!props.wizardState.restoreAccount;
  const isServerless = props.wizardState.clusterType === 'serverless';

  const redshiftProps = props.wizardState.snapshot.redshiftSnapshotProperties;
  const awsBackupVaultInfo = useMemo(() => {
    if (!redshiftProps) return undefined;
    return {
      cloudProvider: Provider.Aws,
      region: redshiftProps.region || props.wizardState.initialRegionName,
    };
  }, [redshiftProps, props.wizardState.initialRegionName]);

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      vpcSecurityGroupIds: [],
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  useEffect(() => {
    if (
      !props.wizardState.restoreAccount &&
      props.wizardState.accounts?.length
    ) {
      props.setWizardState((state) => ({
        ...state,
        restoreAccount: state.accounts?.[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.accounts, props.wizardState.restoreAccount]);

  const isNodeTypeChanged =
    !!props.wizardState.nodeType &&
    props.wizardState.nodeType !== props.wizardState.originalNodeType;
  const needsExplicitNodeCount =
    isNodeTypeChanged && !props.wizardState.numberOfNodes;

  const canGoNext = () => {
    if (!hasSourceAccount) {
      return false;
    }

    if (isServerless) {
      return !isRestoring && !!props.wizardState.namespaceName;
    }

    return (
      !isRestoring &&
      !!props.wizardState.clusterIdentifier &&
      !needsExplicitNodeCount
    );
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    return dal.restore.redshift
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          region: props.wizardState.regionName || '',
          clusterIdentifier: props.wizardState.clusterIdentifier || '',
          namespaceName: props.wizardState.namespaceName || '',
          nodeType: props.wizardState.nodeType || '',
          numberOfNodes: props.wizardState.numberOfNodes || 0,
          vpcSecurityGroupIds: props.wizardState.vpcSecurityGroupIds,
          port: props.wizardState.port ?? 5439,
          encryptionKey: getEncryptionKey(props.wizardState.encryption),
          tags,
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${isServerless ? props.wizardState.namespaceName : props.wizardState.clusterIdentifier}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore Redshift resource: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      hideStepper
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
          awsBackupVaultInfo={awsBackupVaultInfo}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        {!hasSourceAccount && (
          <Alert severity='info' className='mb-[20px]'>
            <Typography variant='body2'>
              Redshift backups are stored in AWS Backup Vault in the source
              account and can only be restored to the same account.
            </Typography>
          </Alert>
        )}

        {isServerless ? (
          <Stack direction='column' gap='24px' className='mt-[20px]'>
            <Alert severity='warning'>
              <Typography variant='body2'>
                Serverless restore is destructive — it will overwrite all data
                in the selected namespace.
              </Typography>
            </Alert>
            <RedshiftServerlessNamespaceSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              namespaceName={props.wizardState.namespaceName}
              onChange={(namespaceName) =>
                props.setWizardState((state) => ({
                  ...state,
                  namespaceName,
                }))
              }
            />
          </Stack>
        ) : (
          <Stack direction='column' gap='24px' className='mt-[20px]'>
            <FormTextField
              title='Cluster identifier'
              dataTestId='clusterIdentifierTextField'
              validation={requiredValidation}
              placeholder='Enter cluster identifier'
              value={props.wizardState.clusterIdentifier || ''}
              onChange={(e) =>
                props.setWizardState((state) => ({
                  ...state,
                  clusterIdentifier: e.target.value,
                }))
              }
            />

            <Stack direction='row' gap='36px'>
              <RedshiftNodeTypeSelect
                nodeType={props.wizardState.nodeType}
                originalNodeType={props.wizardState.originalNodeType}
                onChange={(nodeType) =>
                  props.setWizardState((state) => ({
                    ...state,
                    nodeType,
                    numberOfNodes: undefined,
                  }))
                }
              />
              <RedshiftNodeCountSelect
                nodeType={props.wizardState.nodeType}
                originalNodeType={props.wizardState.originalNodeType}
                numberOfNodes={props.wizardState.numberOfNodes}
                originalNumberOfNodes={props.wizardState.originalNumberOfNodes}
                onChange={(numberOfNodes) =>
                  props.setWizardState((state) => ({
                    ...state,
                    numberOfNodes,
                  }))
                }
              />
            </Stack>

            <Stack direction='row' gap='36px'>
              <FormControl size='small' className='flex-1'>
                <FormLabel>Port</FormLabel>
                <TextField
                  data-testid='portTextField'
                  size='small'
                  type='number'
                  placeholder='5439'
                  value={props.wizardState.port ?? ''}
                  onChange={(e) =>
                    props.setWizardState((state) => ({
                      ...state,
                      port: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    }))
                  }
                />
              </FormControl>
            </Stack>
          </Stack>
        )}
      </Box>

      {props.wizardState.regionName && hasSourceAccount && (
        <>
          <Divider />
          <CardContent className='px-0'>
            <EncryptionComponent
              accountId={props.wizardState.restoreAccount!.id}
              regionName={props.wizardState.regionName}
              onChange={(encryption) =>
                props.setWizardState((state) => ({ ...state, encryption }))
              }
              encryption={props.wizardState.encryption}
            />
          </CardContent>
        </>
      )}

      <Divider />

      <CardContent>
        <Stack className='mx-[16px]' gap='24px'>
          <TagsSection
            tags={props.wizardState.tags}
            initialTags={props.wizardState.initialTags}
            keepOriginalTags={props.wizardState.keepOriginalTags}
            onChange={(tags: Record<string, string>) =>
              props.setWizardState((state) => ({ ...state, tags }))
            }
            setKeepOriginalTags={(keepOriginalTags: boolean) =>
              props.setWizardState((state) => ({
                ...state,
                keepOriginalTags,
              }))
            }
          />
        </Stack>
      </CardContent>
    </StepContainer>
  );
};
